import { useEffect, useState } from 'react';
import {  Container, Grid, CircularProgress, Chip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { CustomModal } from 'components';
import { services } from 'redux/services';

interface ViewProps {
    open: boolean;
    onClose: () => void;
    selectedData: any;
}

const FdaDrugViewModal: React.FC<ViewProps> = ({ open, onClose, selectedData }) => {
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState<boolean>(false);
    const [drugDetails, setDrugDetails] = useState<any>(null);

    useEffect(() => {
        if (open && selectedData?.appl_no) {
            fetchDrugDetails(selectedData.appl_no);
        }
    }, [open, selectedData]);

    const fetchDrugDetails = async (applNo: string): Promise<void> => {
        setLoading(true);
        try {
            const response: any = await services.getFDADrugsDetail(applNo);
            setDrugDetails(response.result);
        } catch (error) {
            console.error('Error fetching drug details:', error);
        } finally {
            setLoading(false);
        }
    };

    const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
            <Grid item xs={4}>
                <strong>{label}:</strong>
            </Grid>
            <Grid item xs={8}>
                {value || 'N/A'}
            </Grid>
        </Grid>
    );

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={selectedData?.drug_name}
            size='lg'
        >
            <Container>
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                        <CircularProgress />
                    </div>
                ) : drugDetails ? (
                    <div>
                        {/* Basic Information */}
                        <h3><FormattedMessage id="basic-information" /></h3>
                        <InfoRow
                            label={formatMessage({ id: "application-no" })}
                            value={drugDetails.basic_info?.appl_no}
                        />
                        <InfoRow
                            label={formatMessage({ id: "product-no" })}
                            value={drugDetails.basic_info?.product_no}
                        />
                        <InfoRow
                            label={formatMessage({ id: "drug-name" })}
                            value={drugDetails.basic_info?.drug_name}
                        />
                        <InfoRow
                            label={formatMessage({ id: "active-ingredient" })}
                            value={drugDetails.basic_info?.active_ingredient}
                        />
                        <InfoRow
                            label={formatMessage({ id: "form" })}
                            value={drugDetails.basic_info?.form}
                        />
                        <InfoRow
                            label={formatMessage({ id: "strength" })}
                            value={drugDetails.basic_info?.strength}
                        />
                        <InfoRow
                            label={formatMessage({ id: "sponsor" })}
                            value={drugDetails.basic_info?.sponsor}
                        />
                        <InfoRow
                            label={formatMessage({ id: "application-type" })}
                            value={drugDetails.basic_info?.application_type}
                        />
                        <InfoRow
                            label={formatMessage({ id: "marketing-status" })}
                            value={
                                <Chip
                                    label={drugDetails.basic_info?.marketing_status}
                                    color="primary"
                                    size="small"
                                />
                            }
                        />
                        <InfoRow
                            label={formatMessage({ id: "reference-drug" })}
                            value={drugDetails.basic_info?.is_reference_drug ? 'Yes' : 'No'}
                        />

                        {/* Description */}
                        {drugDetails.descriptions && (
                            <>
                                <hr />
                                <h3><FormattedMessage id="description" /></h3>
                                {drugDetails.descriptions.summary && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <strong>Summary:</strong>
                                        <p>{drugDetails.descriptions.summary}</p>
                                    </div>
                                )}
                                {drugDetails.descriptions.indications && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <strong>Indications:</strong>
                                        <p>{drugDetails.descriptions.indications}</p>
                                    </div>
                                )}
                                {drugDetails.descriptions.important_info && drugDetails.descriptions.important_info.length > 0 && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <strong style={{ color: 'red' }}>Important Information:</strong>
                                        <ul>
                                            {drugDetails.descriptions.important_info.map((info: string, idx: number) => (
                                                <li key={idx}>{info}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Review Documents */}
                        {drugDetails.review_documents && drugDetails.review_documents.length > 0 && (
                            <>
                                <hr />
                                <h3><FormattedMessage id="review-documents" /></h3>
                                <ul>
                                    {drugDetails.review_documents.map((doc: any, idx: number) => (
                                        <li key={idx}>
                                            <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                                {doc.title || 'Document'}
                                            </a>
                                            <br />
                                            <small>Type: {doc.type}</small>
                                            {doc.description && (
                                                <>
                                                    <br />
                                                    <small>{doc.description}</small>
                                                </>
                                            )}
                                            {doc.date && (
                                                <>
                                                    <br />
                                                    <small>Date: {new Date(doc.date).toLocaleDateString()}</small>
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}

                        {/* Submission History */}
                        {drugDetails.submission_notes && drugDetails.submission_notes.length > 0 && (
                            <>
                                <hr />
                                <h3><FormattedMessage id="submission-history" /></h3>
                                <ul>
                                    {drugDetails.submission_notes.slice(0, 5).map((submission: any, idx: number) => (
                                        <li key={idx}>
                                            <strong>{submission.status}</strong> - {submission.type} ({submission.class})
                                            {submission.date && (
                                                <>
                                                    <br />
                                                    <small>Date: {new Date(submission.date).toLocaleDateString()}</small>
                                                </>
                                            )}
                                            {submission.summary && (
                                                <>
                                                    <br />
                                                    <small>{submission.summary}</small>
                                                </>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                ) : (
                    <p>No details available</p>
                )}
            </Container>

        </CustomModal>
    );
};

export default FdaDrugViewModal;